import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faTriangleExclamation, faFolderOpen, faXmark, faChevronDown, faPersonSwimming, faLocationDot } from '@fortawesome/free-solid-svg-icons';
import { useSwimmerAPI } from '../context/SwimmerAPIContext';
import { parseEventName, sortStrokeValues, collectStrokes, collectDistances } from '../../services/utils/EventOrdering';
import { calculateAge } from '../../services/utils/ageCalculation';
import { normalizeGender } from '../../services/utils/genderNormalizer';
import { STANDARD_CATEGORY_ORDER, getStandardCategory } from '../../services/utils/standardCategories';
import { useGroupBy, groupLabel } from '../hooks/useEventFilters';
import { useCollapsible } from '../hooks/useCollapsible';
import StandardsModal from './StandardsModal';
import { EventFilterBar, FilterChipGroup, SegmentedToggle } from './filters';
const STATUS_OPTIONS = ['qualifies', 'consideration', 'slower'];
const statusLabel = (status) => status === 'qualifies' ? '✓ Qualifies' : status === 'consideration' ? '~ Consider' : '✕ Slower';
const getFilteredCountyTimes = (birthYear, gender, allTimes) => {
    const age = calculateAge(birthYear);
    const normGender = normalizeGender(gender);
    const filtered = {};
    Object.keys(allTimes).forEach(key => {
        const parts = key.split('_');
        const courseIndex = parts.findIndex(p => p === '25m' || p === '50m');
        if (courseIndex <= 0)
            return;
        const course = parts[courseIndex];
        const keyGender = normalizeGender(parts[courseIndex + 1] || '');
        const ageFrom = parseInt(parts[courseIndex + 2]);
        const ageTo = parseInt(parts[courseIndex + 3]);
        if (keyGender === normGender && age >= ageFrom && age <= ageTo) {
            const event = parts.slice(0, courseIndex).join(' ');
            filtered[`${event}_${course}`] = allTimes[key];
        }
    });
    return filtered;
};
const categoryLabel = (category, names, store) => {
    const countries = [...new Set(names.map(n => store[n]?.country).filter(Boolean))];
    return countries.length === 1 ? `${category} — ${countries[0]}` : category;
};
const CountyComparison = ({ swimmerData, countyTimesStore, activeStandards: _activeStandards, onActiveStandardsChange: _onActiveStandardsChange, selectedStrokes = [], onSelectedStrokesChange, selectedDistances = [], onSelectedDistancesChange, defaultStandardNames, onLoadCountyTimes, onClearOneCounty, onCountySelected, onComparisonChange, loading, setLoading, showToast, }) => {
    const api = useSwimmerAPI();
    const countyNames = Object.keys(countyTimesStore);
    const [selectedCounty, setSelectedCounty] = useState('');
    const [comparison, setComparison] = useState(null);
    const [lookAhead, setLookAhead] = useState(false);
    const [showStandardsModal, setShowStandardsModal] = useState(false);
    const [groupBy, setGroupBy] = useGroupBy();
    const [collapsedGroups, toggleGroup] = useCollapsible();
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    // Ages are calculated against 31st Dec of the calendar year, so the look-ahead is simply
    // the next one. Read per render rather than at module load, so a session left open over
    // New Year doesn't keep offering last year's standards.
    const currentYear = new Date().getFullYear();
    const lookAheadOptions = [
        { value: 'current', label: String(currentYear) },
        { value: 'next', label: String(currentYear + 1) },
    ];
    useEffect(() => { setComparison(null); onComparisonChange?.(null); }, [swimmerData.tiref]);
    const handleSelectCounty = (county) => {
        setSelectedCounty(county);
        onCountySelected(county);
    };
    useEffect(() => {
        if (countyNames.length === 0) {
            setSelectedCounty('');
            return;
        }
        if (selectedCounty && countyNames.includes(selectedCounty))
            return;
        if (swimmerData.preferredCounty && countyNames.includes(swimmerData.preferredCounty)) {
            setSelectedCounty(swimmerData.preferredCounty);
            return;
        }
        // Only auto-pick when there's a single standard loaded (no real choice for the user to make).
        // With multiple standards and no saved preference, leave unselected so the user explicitly picks one.
        if (countyNames.length === 1) {
            setSelectedCounty(countyNames[0]);
        }
        else {
            setSelectedCounty('');
        }
    }, [countyTimesStore, swimmerData.preferredCounty, selectedCounty]);
    const handleCompare = async (lookAheadOverride) => {
        if (!swimmerData.birthYear || !swimmerData.gender)
            return;
        if (!selectedCounty || !countyNames.includes(selectedCounty) || !countyTimesStore[selectedCounty])
            return;
        const effectiveLookAhead = lookAheadOverride ?? lookAhead;
        setLoading(true);
        try {
            const allTimes = countyTimesStore[selectedCounty]?.times ?? {};
            const age = calculateAge(swimmerData.birthYear);
            const targetAge = effectiveLookAhead ? age + 1 : age;
            const adjustedBirthYear = effectiveLookAhead
                ? (parseInt(swimmerData.birthYear) - 1).toString()
                : swimmerData.birthYear;
            const filtered = getFilteredCountyTimes(adjustedBirthYear, swimmerData.gender, allTimes);
            if (Object.keys(filtered).length === 0) {
                showToast(`No ${selectedCounty} times found for Age ${targetAge}, Gender ${swimmerData.gender}, 50m course.`, 'error');
                return;
            }
            const result = await api.compareWithCountyTimes(swimmerData, filtered);
            setComparison(result);
            onComparisonChange?.(result);
        }
        catch (error) {
            showToast(`Error comparing times: ${error.message}`, 'error');
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        if (!selectedCounty || !swimmerData.birthYear || !swimmerData.gender)
            return;
        if (!countyTimesStore[selectedCounty])
            return;
        handleCompare(lookAhead);
    }, [selectedCounty, lookAhead]); // eslint-disable-line react-hooks/exhaustive-deps
    return (React.createElement("div", { className: "tab-content active" },
        React.createElement("div", { style: { marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
            countyNames.length === 0 && (React.createElement("span", { style: { color: 'var(--danger)', fontSize: '0.9em' } },
                React.createElement(FontAwesomeIcon, { icon: faTriangleExclamation }),
                " No standards loaded")),
            React.createElement("button", { onClick: () => setShowStandardsModal(true), disabled: loading, className: "btn-ghost", style: { marginLeft: 'auto', padding: '5px 15px', fontSize: '0.9em', color: 'var(--primary)', whiteSpace: 'nowrap' } },
                React.createElement(FontAwesomeIcon, { icon: faFolderOpen }),
                " ",
                countyNames.length > 0 ? `Standards (${countyNames.length})` : 'Load Standards'),
            showStandardsModal && (React.createElement(StandardsModal, { countyTimesStore: countyTimesStore, defaultStandardNames: defaultStandardNames, onClearOneCounty: onClearOneCounty, onLoadCountyTimes: onLoadCountyTimes, onClose: () => setShowStandardsModal(false), loading: loading }))),
        swimmerData.region && (React.createElement("div", { style: { fontSize: '0.85em', color: 'var(--text-secondary)' } },
            "Swimmer region: ",
            React.createElement("strong", null, swimmerData.region))),
        React.createElement("div", { className: "comparison-controls", style: { display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' } },
            countyNames.length > 1 && (React.createElement("div", { className: "rankings-county-filter standards-select-label" },
                React.createElement(FontAwesomeIcon, { icon: faLocationDot, className: "rankings-county-icon" }),
                React.createElement("select", { value: selectedCounty, onChange: e => handleSelectCounty(e.target.value), className: "rankings-county-select" },
                    React.createElement("option", { value: "" }, "Select standard..."),
                    STANDARD_CATEGORY_ORDER
                        .map(category => {
                        const namesInCategory = countyNames.filter((name) => getStandardCategory(name) === category);
                        if (namesInCategory.length === 0)
                            return null;
                        return (React.createElement("optgroup", { key: category, label: categoryLabel(category, namesInCategory, countyTimesStore) }, namesInCategory.map((name) => (React.createElement("option", { key: name, value: name }, countyTimesStore[name] ? `${name} (${countyTimesStore[name].year})` : name)))));
                    })))),
            countyNames.length === 1 && (React.createElement("div", { className: "rankings-county-filter" },
                React.createElement(FontAwesomeIcon, { icon: faLocationDot, className: "rankings-county-icon" }),
                React.createElement("div", { className: "rankings-county-select", style: { display: 'flex', alignItems: 'center' } },
                    countyNames[0],
                    countyTimesStore[countyNames[0]] && (React.createElement("span", { className: "swimmer-tag-badge", style: { marginLeft: '0.5em' } }, countyTimesStore[countyNames[0]].year))))),
            React.createElement(SegmentedToggle, { options: lookAheadOptions, value: lookAhead ? 'next' : 'current', onChange: next => setLookAhead(next === 'next') })),
        comparison && (() => {
            const getStatus = (comp) => {
                if (comp.isFaster === null)
                    return 'no-time';
                if (comp.isFaster)
                    return 'qualifies';
                if (comp.meetsConsideration)
                    return 'consideration';
                return 'slower';
            };
            const filtered = comparison.comparisons.filter(comp => {
                const { stroke, distance } = parseEventName(comp.event);
                const strokeMatch = selectedStrokes.length === 0 || selectedStrokes.includes(stroke);
                const distanceMatch = selectedDistances.length === 0 || selectedDistances.includes(distance.toString());
                const statusMatch = selectedStatuses.length === 0 || selectedStatuses.includes(getStatus(comp));
                return strokeMatch && distanceMatch && statusMatch;
            });
            // Build groups
            const byCourse = new Map();
            const byStroke = new Map();
            for (const comp of filtered) {
                if (!byCourse.has(comp.standardCourse))
                    byCourse.set(comp.standardCourse, []);
                byCourse.get(comp.standardCourse).push(comp);
                const { stroke } = parseEventName(comp.event);
                if (!byStroke.has(stroke))
                    byStroke.set(stroke, []);
                byStroke.get(stroke).push(comp);
            }
            const courseKeys = ['50m', '25m'].filter(c => byCourse.has(c));
            const strokeKeys = sortStrokeValues(Array.from(byStroke.keys()));
            const groupKeys = groupBy === 'course' ? courseKeys : strokeKeys;
            const groupComps = (key) => groupBy === 'course' ? byCourse.get(key) : byStroke.get(key);
            const renderRow = (comp) => (React.createElement("tr", { key: `${comp.standardCourse}-${comp.event}` },
                React.createElement("td", { "data-label": "Event", className: "card-event-td" },
                    React.createElement("div", { className: "card-event-header" },
                        React.createElement("span", null,
                            groupBy !== 'course' && (React.createElement(React.Fragment, null,
                                React.createElement("span", { className: "course-pill", style: { marginLeft: '0.4rem' } }, comp.standardCourse === '50m' ? 'LC' : 'SC'),
                                "\u00A0\u00A0")),
                            comp.event),
                        React.createElement("span", { className: "mobile-supplement card-inline-time" }, comp.swimmerTime)),
                    React.createElement("div", { className: "mobile-supplement card-meta-row" },
                        comp.isFaster === null ? ('-') : comp.isFaster ? (React.createElement("span", { className: "faster" },
                            React.createElement(FontAwesomeIcon, { icon: faCheck }),
                            " Qualifies")) : comp.meetsConsideration ? (React.createElement("span", { style: { color: 'var(--warning-color, orange)' } }, "~ Consideration")) : (React.createElement("span", { className: "slower" },
                            React.createElement(FontAwesomeIcon, { icon: faXmark }),
                            " Slower")),
                        ' • ',
                        "Diff: ",
                        comp.difference,
                        ' • ',
                        "Course:",
                        comp.course,
                        React.createElement("div", { className: "mobile-supplement card-meta-row", style: { color: 'var(--gray-400)' } },
                            "Qualifying: ",
                            comp.standardTime,
                            comp.considerationTime ? ` • Consider: ${comp.considerationTime}` : '',
                            comp.ageCategory ? ` • Age: ${comp.ageCategory}` : ''))),
                React.createElement("td", { "data-label": "Course", className: "hide-on-mobile" }, comp.course),
                React.createElement("td", { "data-label": "Swimmer Time", className: "hide-on-mobile" },
                    React.createElement("strong", null, comp.swimmerTime),
                    React.createElement("br", null),
                    React.createElement("small", { style: { color: 'var(--gray-400)' } },
                        "Qual: ",
                        comp.standardTime,
                        comp.considerationTime ? ` • Consider: ${comp.considerationTime}` : '')),
                React.createElement("td", { "data-label": "Age Category", className: "hide-on-mobile" }, comp.ageCategory || '-'),
                React.createElement("td", { "data-label": "Difference", className: "hide-on-mobile" }, comp.difference),
                React.createElement("td", { "data-label": "Status", className: "hide-on-mobile", style: { whiteSpace: 'nowrap' } }, comp.isFaster === null ? ('-') : comp.isFaster ? (React.createElement("span", { className: "faster" },
                    React.createElement(FontAwesomeIcon, { icon: faCheck }),
                    " Qualifies")) : comp.meetsConsideration ? (React.createElement("span", { style: { color: 'var(--warning-color, orange)' } }, "~ Consideration")) : (React.createElement("span", { className: "slower" },
                    React.createElement(FontAwesomeIcon, { icon: faXmark }),
                    " Slower")))));
            return (React.createElement(React.Fragment, null,
                React.createElement(EventFilterBar, { strokes: collectStrokes(comparison.comparisons.map(comp => comp.event)), selectedStrokes: selectedStrokes, onStrokesChange: onSelectedStrokesChange, distances: collectDistances(comparison.comparisons.map(comp => comp.event)), selectedDistances: selectedDistances, onDistancesChange: onSelectedDistancesChange, groupBy: groupBy, onGroupByChange: setGroupBy, extraFilters: React.createElement(FilterChipGroup, { label: "Status", options: [...STATUS_OPTIONS], value: selectedStatuses, onChange: setSelectedStatuses, renderLabel: statusLabel }) }),
                React.createElement("div", { style: { marginBottom: '10px', padding: '10px', backgroundColor: 'var(--card-bg)', borderRadius: '5px' } },
                    React.createElement("strong", null, "Comparing Against: "),
                    selectedCounty,
                    countyTimesStore[selectedCounty] ? ` ${countyTimesStore[selectedCounty].year}` : '',
                    " Standards",
                    swimmerData.birthYear && (React.createElement(React.Fragment, null,
                        ' — ',
                        "Age ",
                        lookAhead ? calculateAge(swimmerData.birthYear) + 1 : calculateAge(swimmerData.birthYear),
                        lookAhead && (React.createElement("span", { style: { marginLeft: '0.5rem', color: 'var(--primary)', fontWeight: 'bold' } }, "(Next Year)"))))),
                groupKeys.map((groupKey, index) => (React.createElement("div", { key: groupKey, className: `club-group${index > 0 ? ' club-group-spaced' : ''}` },
                    React.createElement("div", { className: "club-header", onClick: () => toggleGroup(groupKey) },
                        React.createElement("h3", { className: "club-header-title" },
                            React.createElement(FontAwesomeIcon, { icon: faPersonSwimming }),
                            " ",
                            groupLabel(groupBy, groupKey)),
                        React.createElement(FontAwesomeIcon, { icon: faChevronDown, className: `chevron-icon${!collapsedGroups.has(groupKey) ? ' expanded' : ''}` })),
                    !collapsedGroups.has(groupKey) && (React.createElement("div", { className: "table-container" },
                        React.createElement("table", null,
                            React.createElement("thead", null,
                                React.createElement("tr", null,
                                    React.createElement("th", null, "Event"),
                                    React.createElement("th", null, "Course"),
                                    React.createElement("th", null, "Swimmer Time"),
                                    React.createElement("th", null, "Age Category"),
                                    React.createElement("th", null, "Difference"),
                                    React.createElement("th", null, "Status"))),
                            React.createElement("tbody", null, groupComps(groupKey).map(renderRow))))))))));
        })()));
};
export default CountyComparison;
